"use client";

import {
  useInsuranceConnectorContext,
  useInsuranceConnectorMatches,
  useInsuranceConnectorSend,
} from "@frontend/components/InsuranceConnector/insurance-connector-provider";
import { getUnderAgeRejectionAge } from "@frontend/components/InsuranceConnector/machine/guards/config.guard";
import {
  type NetworkData,
  getNetworkStatus,
  getPracticesByOrgId,
} from "@frontend/lib/actions/actions";
import { getProductByProductId } from "@frontend/lib/actions/market-graphql-queries";
import { trackEvent } from "@frontend/lib/analytics";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { Button } from "@frontend/shared/components/ui/button";
import { cn } from "@frontend/shared/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { Ban, Loader2 } from "lucide-react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef } from "react";

function processNetworkData(
  networkData: Array<{
    inNetwork: boolean;
    practiceId: number;
    providerId: number;
  }>,
): Record<number, number[]> {
  return networkData.reduce(
    (accum, item) => {
      if (!item.inNetwork) return accum;
      const providerIdsForPractice = accum[item.practiceId] ?? [];
      accum[item.practiceId] = Array.from(
        new Set([...providerIdsForPractice, item.providerId]),
      );
      return accum;
    },
    {} as Record<number, number[]>,
  );
}

function hasRequiredConsumerFields(
  consumer: { fname?: string; lname?: string; dob?: string } | null | undefined,
): boolean {
  return !!(consumer?.fname && consumer?.lname && consumer?.dob);
}

export function EligibilityButton({
  children,
  link,
  orgId,
  classNames,
}: {
  children: React.ReactNode;
  link: string;
  orgId: number;
  classNames?: string;
}) {
  const queryClient = useQueryClient();
  const t = useTranslations("Shop");
  const sendInsurance = useInsuranceConnectorSend();
  const { providerOonStatus } = useInsuranceConnectorContext();
  const machineIsConnected = useInsuranceConnectorMatches("connected");

  const configFlags =
    queryClient.getQueryData<Record<string, boolean>>(["configFlags", orgId]) ??
    {};
  const eligibilityCheckResult = useBoundStore.use.eligibilityCheckResult();
  const setPricingMap = useBoundStore.use.setPricingMap();
  const insurances = useBoundStore.use.insurances();
  const pricingMap = useBoundStore.use.pricingMapEntries();
  const consumer = useBoundStore.use.consumer();
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const isInsuranceValidated = useBoundStore.use.isInsuranceValidated();

  const params = useParams();
  const isOnProductPage = params.productId != null;
  const productId = Number(params.productId);

  // Fetch product HRTs
  const { data: productsAssociatedHrts } = useQuery({
    queryKey: ["getHrtsFromProductId", productId, orgId],
    queryFn: () => getProductByProductId(productId, orgId),
    select: (data) => data.findFirstProducts?.productToHrt,
    staleTime: 0,
    enabled: isOnProductPage,
  });

  // Fetch storefront bookable practices
  const { data: storefrontBookablePracticeIds } = useQuery({
    queryKey: ["getPracticesByOrgId", orgId],
    queryFn: () => getPracticesByOrgId(orgId),
    select: (data) =>
      new Set(
        (data ?? [])
          .filter((practice) => practice.isAvailableFromStorefront)
          .map((practice) => practice.practiceId),
      ),
  });

  // Filter HRTs by if they are bookable on the storefront
  const bookableProcedures = productsAssociatedHrts?.filter(
    (hrt) =>
      hrt.hrt.ehrPrimaryAppointmentTypeId?.length > 0 &&
      hrt.hrt.isBookableOnStorefront,
  );

  const firstBookableHrtId = bookableProcedures?.[0]?.hrtId;

  const hasValidConsumer = hasRequiredConsumerFields(consumer);
  // Only run the provider-network lookup when the insurance is *active* and
  // in-network (`isInsuranceValidated === true`, set exclusively by
  // `syncAllToStore` on the active verification outcome). Inactive,
  // out-of-network, and unverified-no-flag insurances are now persisted to
  // the store so the saved card stays visible — but the consumer has no
  // verified coverage, so we must not call `getNetworkStatus` against them
  // and must not transition the connector machine into the
  // `allProvidersOonResult` state on the back of that lookup.
  const shouldRunProviderOonCheck =
    isOnProductPage &&
    firstBookableHrtId != null &&
    insurances.length > 0 &&
    isInsuranceValidated &&
    !isPendingPricesMode;

  // Shape the shared raw network data into this call site's by-HRT lookup.
  // Memoized on firstBookableHrtId so React Query doesn't re-run select (and
  // hand downstream a fresh object) on every render.
  const selectInNetworkProviderIdsByHrt = useCallback(
    (networkData: NetworkData[]) =>
      firstBookableHrtId != null
        ? { [firstBookableHrtId]: processNetworkData(networkData) }
        : {},
    [firstBookableHrtId],
  );

  // Fetch network status for this specific product's HRT.
  // Shares ["orgNetworkStatus", orgId, hrtId, consumerId, memberId] with the
  // scheduling page's check so React Query dedups them for the same
  // org/patient/HRT. The queryFn returns the raw NetworkData[] the shared
  // cache stores; each call site derives its own shape via `select`.
  const { data: inNetworkProviderIdsByHrt } = useQuery({
    queryKey: [
      "orgNetworkStatus",
      orgId,
      firstBookableHrtId,
      consumer?.id,
      insurances[0]?.memberId,
    ],
    queryFn: () => {
      if (!firstBookableHrtId) return [];
      return getNetworkStatus(consumer, insurances, orgId, firstBookableHrtId);
    },
    select: selectInNetworkProviderIdsByHrt,
    enabled: shouldRunProviderOonCheck && hasValidConsumer,
    // Scoped guardrails for the eligibility/network round-trip: coalesce
    // in-flight duplicates for a few minutes and don't auto-refetch on
    // window focus / reconnect. See PR for rationale.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const inNetworkProviderIdsByPractice =
    firstBookableHrtId != null
      ? inNetworkProviderIdsByHrt?.[firstBookableHrtId]
      : undefined;

  const inNetworkProviderIdsByStorefrontBookablePractices = useMemo(
    () =>
      new Map(
        Object.entries(inNetworkProviderIdsByPractice ?? {})
          .map(
            ([practiceId, providerIds]) =>
              [Number(practiceId), new Set(providerIds as number[])] as const,
          )
          .filter(
            ([practiceId]) =>
              storefrontBookablePracticeIds?.has(practiceId) ?? false,
          ),
      ),
    [inNetworkProviderIdsByPractice, storefrontBookablePracticeIds],
  );

  const hasResolvedProviderOonCheck =
    storefrontBookablePracticeIds !== undefined &&
    (inNetworkProviderIdsByPractice !== undefined || !hasValidConsumer);

  const isProviderOonCheckPending =
    shouldRunProviderOonCheck &&
    hasValidConsumer &&
    !hasResolvedProviderOonCheck;

  const allProvidersAreOON =
    isOnProductPage &&
    hasResolvedProviderOonCheck &&
    inNetworkProviderIdsByStorefrontBookablePractices.size === 0 &&
    shouldRunProviderOonCheck;

  // Auto-switch to CASH mode when all providers are OON
  useEffect(() => {
    if (!allProvidersAreOON || firstBookableHrtId == null) return;
    if (pricingMap[firstBookableHrtId]?.mode !== "INSURED") return;

    setPricingMap({
      [firstBookableHrtId]: {
        ...pricingMap[firstBookableHrtId],
        mode: "CASH",
      },
    });
  }, [allProvidersAreOON, firstBookableHrtId, pricingMap, setPricingMap]);

  // ── Sync provider OON check status to the insurance connector machine ──
  const oonSyncRef = useRef<"idle" | "started" | "resolved">("idle");
  const prevCheckKeyRef = useRef<string | null>(null);

  const checkKey = shouldRunProviderOonCheck
    ? `${productId}-${insurances[0]?.memberId}`
    : null;

  useEffect(() => {
    if (checkKey !== prevCheckKeyRef.current) {
      if (prevCheckKeyRef.current !== null) {
        sendInsurance({ type: "PROVIDER_OON_RESET" });
      }
      prevCheckKeyRef.current = checkKey;
      oonSyncRef.current = "idle";
    }

    if (!checkKey || !hasValidConsumer || !machineIsConnected) return;

    if (isProviderOonCheckPending && oonSyncRef.current === "idle") {
      sendInsurance({ type: "PROVIDER_OON_CHECK_STARTED" });
      oonSyncRef.current = "started";
      return;
    }

    if (hasResolvedProviderOonCheck && oonSyncRef.current !== "resolved") {
      sendInsurance({
        type: "PROVIDER_OON_RESOLVED",
        allOON: allProvidersAreOON,
      });
      oonSyncRef.current = "resolved";
    }
  }, [
    checkKey,
    hasValidConsumer,
    machineIsConnected,
    isProviderOonCheckPending,
    hasResolvedProviderOonCheck,
    allProvidersAreOON,
    sendInsurance,
  ]);

  useEffect(() => {
    return () => {
      sendInsurance({ type: "PROVIDER_OON_RESET" });
    };
  }, [sendInsurance]);

  // Block book button while provider OON check is in progress
  if (isProviderOonCheckPending || providerOonStatus === "checking") {
    return (
      <div className="flex flex-col gap-3 items-center w-full">
        <div className="w-full opacity-60 pointer-events-none">{children}</div>
        <div className="flex flex-row gap-2 items-center">
          <Loader2 className="w-4 h-4 animate-spin" />
          <p className="text-sm font-medium text-[#6D757F]">
            {t("VerifyingYourProviderNetworkStatus")}
          </p>
        </div>
      </div>
    );
  }

  // Block book button when all providers are OON (minimized panel shows the modal)
  if (providerOonStatus === "all_oon") {
    return (
      <div className="flex flex-col gap-3 items-center w-full">
        <div
          className="w-full opacity-60 pointer-events-none"
          aria-disabled="true"
        >
          {children}
        </div>
        <FailedEligibilityCheckResult messageType="provider-oon" />
      </div>
    );
  }

  // Legacy eligibility checks (under age, state, etc.) - only when flag enabled
  if (!configFlags?.storefront_offboard_consumers) {
    return <>{children}</>;
  }

  if (eligibilityCheckResult.result === "passed") {
    if (isOnProductPage) {
      return (
        <div className={cn("flex flex-col gap-2", classNames)}>
          {children}
          <p className="text-sm font-semibold">You&apos;re eligible!</p>
        </div>
      );
    }
    // On non-product pages (category list / featured carousel) the caller
    // renders the full CTA row itself — just surface the children at full
    // width so Buy Now / View Details can stretch to fill.
    return <div className={cn("w-full", classNames)}>{children}</div>;
  }

  if (eligibilityCheckResult.result === "failed") {
    if (isOnProductPage) {
      return (
        <div className="flex flex-col gap-3 items-center w-full">
          <div
            className="w-full opacity-60 pointer-events-none"
            aria-disabled="true"
          >
            {children}
          </div>
          <FailedEligibilityCheckResult showSpecialMessage={true} />
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-3 w-full">
        <Link
          href={link}
          className={cn(
            "border border-black/40 rounded-full p-4 text-lg w-full text-center text-white bg-black",
            classNames,
          )}
        >
          View Details
        </Link>
        <FailedEligibilityCheckResult />
      </div>
    );
  }

  // Default state - show eligibility check button
  if (isOnProductPage) {
    return (
      <Button
        onClick={() => {
          sendInsurance({ type: "OPEN" });
          trackEvent({
            category: "interaction",
            context: "storefront",
            action: "eligibility_check_button_clicked",
            metadata: {
              productId: isOnProductPage ? productId : "",
              isReturnConsumer: !!consumer?.id,
            },
          });
        }}
        className={cn(
          "border border-black/40 rounded-full p-4 text-lg w-full h-12 text-center text-black bg-white hover:bg-white/80",
          classNames,
        )}
      >
        See if eligible
      </Button>
    );
  }

  return (
    <Link
      href={link}
      onClick={() => {
        trackEvent({
          category: "interaction",
          context: "storefront",
          action: "eligibility_check_button_clicked",
          metadata: {
            productId: isOnProductPage ? productId : "",
            isReturnConsumer: !!consumer?.id,
          },
        });
      }}
      className={cn(
        "border border-black/40 rounded-full p-4 text-lg w-full text-center",
        classNames,
      )}
    >
      See if eligible
    </Link>
  );
}

export const FailedEligibilityCheckResult = ({
  classNames,
  showSpecialMessage = false,
  messageType = "default-eligibility-check",
}: {
  classNames?: string;
  showSpecialMessage?: boolean;
  messageType?: "default-eligibility-check" | "provider-oon";
}) => {
  const eligibilityCheckResult = useBoundStore.use.eligibilityCheckResult();
  const consumer = useBoundStore.use.consumer();
  const minimumAge = useBoundStore.use.minimumAge();
  const sendInsurance = useInsuranceConnectorSend();
  const connectorContext = useInsuranceConnectorContext();
  const t = useTranslations("Eligibility");

  // Prefer the state captured on the eligibility result itself (set by the
  // insurance connector when the user was rejected) and fall back to the
  // consumer slice for legacy callers that still write there.
  const state = eligibilityCheckResult.state ?? consumer.state;

  const DetermineMessage = () => {
    switch (messageType) {
      case "default-eligibility-check":
        return (
          <p className="text-sm font-semibold px-2">
            {eligibilityCheckResult.code === "under_aged" &&
              t("UnderAge.Tag", {
                // Same live source as the connector's rejection screen, so
                // the two can't disagree; the store value covers callers
                // whose connector has no minimum age.
                age: getUnderAgeRejectionAge(connectorContext) ?? minimumAge,
              })}
            {eligibilityCheckResult.code === "state_not_available" &&
              t("StateNotAvailable.Tag", { state })}
            {/* Clicking the button OPENs the connector, which routes a
                referred minor back to the consent-form screen. */}
            {eligibilityCheckResult.code === "minor_consent_referral" &&
              t("MinorConsentReferral.Tag")}
            {/* {eligibilityCheckResult.code === "suicidal_thoughts" &&
              t("SuicidalThoughts.Tag")} */}
            {/* {eligibilityCheckResult.code === "has_medicaid" &&
              t("HasMedicaid.Tag")} */}
            {eligibilityCheckResult.code === "other" && t("Other.Tag")}
          </p>
        );
      case "provider-oon":
        return (
          <p className="text-sm font-semibold px-2 max-w-full text-wrap">
            {t("ProviderOON.Tag")}
          </p>
        );
    }
  };

  return (
    <>
      <button
        type="button"
        className={cn(
          "bg-[#F4F4F4] rounded-lg py-4 px-6 text-sm flex items-center justify-center w-full",
          classNames,
        )}
        onClick={() => {
          sendInsurance({ type: "OPEN" });
        }}
      >
        <Ban />
        {(messageType === "provider-oon" ||
          (eligibilityCheckResult.result === "failed" &&
            eligibilityCheckResult.code)) && <DetermineMessage />}
      </button>
      {/* {eligibilityCheckResult.code === "suicidal_thoughts" &&
        showSpecialMessage && (
          <div className="flex items-center justify-center">
            <p className="text-sm px-2 text-center">
              {t("SuicidalThoughts.SpecialMessage")}
            </p>
          </div>
        )} */}
      {/* {eligibilityCheckResult.code === "has_medicaid" && showSpecialMessage && (
        <div className="flex items-center justify-center">
          <p className="text-sm px-2 text-center">
            {t("HasMedicaid.SpecialMessage")}
          </p>
        </div>
      )} */}
    </>
  );
};
